import { createFileRoute } from '@tanstack/react-router'
import { Typography } from '@/components/ui/typography'
import { Building2Icon, MailIcon, PhoneIcon } from 'lucide-react'
import React, { useRef, useState } from 'react'
import emailjs from '@emailjs/browser'
import * as Sentry from '@sentry/react'
import { Button } from '@/components/ui/button'
import { trackContactFormSubmitted } from '@/lib/track'
import { loadStoredEmail, saveStoredEmail } from '@/lib/userEmail'
import { buildContactHead } from '@/lib/seo/staticPages'

export const Route = createFileRoute('/_layout/kontakt')({
  component: ContactPage,
  head: () => buildContactHead(),
})

function ContactCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 rounded-xl bg-slate-100 p-6">
      <div className="bg-red flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white">{icon}</div>
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        <div className="text-sm text-slate-600">{children}</div>
      </div>
    </div>
  )
}

function ContactForm() {
  const form = useRef<HTMLFormElement>(null)
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)

  const sendEmail = (e: React.SubmitEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (!form.current) return

    setSending(true)
    const formData = new FormData(form.current)
    const name = formData.get('name') as string
    const email = formData.get('email') as string
    const phone = formData.get('phone') as string

    saveStoredEmail(email)
    void emailjs
      .sendForm('service_pui6kxe', 'template_jwjbmje', form.current, {
        publicKey: 'DPcl9qSXUYHZisB-5',
      })
      .then(
        () => {
          // Parse name into firstName and lastName
          const nameParts = name.trim().split(/\s+/)
          const firstName = nameParts[0] || ''
          const lastName = nameParts.slice(1).join(' ') || ''

          void trackContactFormSubmitted({
            firstName,
            lastName,
            email,
            phone: phone || undefined,
          })

          setSent(true)
          setSending(false)
        },
        (error: unknown) => {
          Sentry.captureException(error)
          setSending(false)
        },
      )
  }

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 rounded-xl border border-green-200 bg-green-50 p-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <Typography variant="h3">Děkujeme za zprávu</Typography>
        <p className="text-slate-600">Vaši zprávu jsme přijali a brzy se vám ozveme.</p>
      </div>
    )
  }

  return (
    <form ref={form} className="space-y-4" onSubmit={sendEmail} noValidate>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-2 block text-sm font-medium">
            Jméno a příjmení
          </label>
          <input
            id="name"
            name="name"
            required
            placeholder="Jan Novák"
            className="focus:border-red w-full rounded-lg border border-slate-200 px-4 py-3 placeholder-slate-400 transition-colors focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium">
            E-mail
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            defaultValue={loadStoredEmail()}
            placeholder="jan.novak@email.cz"
            className="focus:border-red w-full rounded-lg border border-slate-200 px-4 py-3 placeholder-slate-400 transition-colors focus:outline-none"
          />
        </div>
      </div>
      <div>
        <label htmlFor="phone" className="mb-2 block text-sm font-medium">
          Telefon
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          placeholder="+420 600 465 213"
          className="focus:border-red w-full rounded-lg border border-slate-200 px-4 py-3 placeholder-slate-400 transition-colors focus:outline-none"
        />
      </div>
      <div>
        <label htmlFor="message" className="mb-2 block text-sm font-medium">
          Zpráva
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={5}
          placeholder="Jak vám můžeme pomoci?"
          className="focus:border-red w-full resize-none rounded-lg border border-slate-200 px-4 py-3 placeholder-slate-400 transition-colors focus:outline-none"
        />
      </div>
      <Button type="submit" variant="red" disabled={sending}>
        {sending ? 'Odesílám...' : 'Odeslat zprávu'}
      </Button>
    </form>
  )
}

function ContactPage() {
  return (
    <div className="container py-8 md:py-12">
      {/* Header */}
      <div className="mb-12">
        <Typography variant="h1" className="mb-4">
          Kontaktujte nás
        </Typography>
        <p className="text-lg text-slate-600">
          Máte dotaz ohledně rezervace nebo chcete nabídnout své ubytování? Jsme tu pro vás 7 dní v týdnu.
        </p>
      </div>

      <div className="grid gap-12 lg:grid-cols-[1fr_1.5fr]">
        {/* Contact Info */}
        <div className="space-y-4">
          <ContactCard icon={<PhoneIcon className="h-5 w-5" />} title="Telefon">
            <a href="tel:+420605131713" className="text-red font-medium hover:underline">
              +420 605 131 713
            </a>
          </ContactCard>

          <ContactCard icon={<MailIcon className="h-5 w-5" />} title="E-mail">
            <a href="mailto:info@matevolno.cz" className="text-red font-medium hover:underline">
              info@matevolno.cz
            </a>
          </ContactCard>

          <ContactCard icon={<Building2Icon className="h-5 w-5" />} title="Provozovatel">
            <p className="font-medium text-slate-700">První chalupářská s.r.o.</p>
            <p>Na Hřebenkách 1380/26, Smíchov</p>
            <p>150&nbsp;00 Praha&nbsp;5</p>
            <p className="mt-2">IČO: 19626487</p>
            <p>
              Zapsaná u Městského soudu v Praze,
              <br />
              oddíl&nbsp;C, vložka&nbsp;389389
            </p>
          </ContactCard>

          {/* <ContactCard
            icon={<ClockIcon className="h-5 w-5" />}
            title="Provozní doba"
          >
            <p>Pondělí – Pátek: 9:00 – 18:00</p>
            <p>Sobota – Neděle: 10:00 – 16:00</p>
          </ContactCard> */}
        </div>

        {/* Contact Form */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-lg md:p-8">
          <Typography variant="h3" className="mb-6">
            Napište nám
          </Typography>
          <ContactForm />
        </div>
      </div>

      {/* Statutory consumer information (§ 14 zák. č. 634/1992 Sb., čl. 12 DSA) */}
      <div className="mt-12 space-y-3 border-t border-slate-200 pt-8 text-xs leading-relaxed text-slate-400">
        <div>
          <p className="mb-0.5 text-slate-500">Mimosoudní řešení spotřebitelských sporů</p>
          <p>
            K mimosoudnímu řešení sporů ze smluv uzavřených s naší společností je věcně příslušná Česká obchodní
            inspekce, se sídlem Gorazdova 1969/24, 120 00 Praha 2,{' '}
            <a href="https://coi.gov.cz" target="_blank" rel="noreferrer" className="underline hover:text-slate-600">
              coi.gov.cz
            </a>
            . Návrh lze podat do jednoho roku ode dne, kdy jste u nás své právo poprvé uplatnili.
          </p>
        </div>

        <div>
          <p className="mb-0.5 text-slate-500">Kontaktní místo podle nařízení o digitálních službách (DSA)</p>
          <p>
            Pro oznámení protiprávního obsahu, stížnosti proti rozhodnutí o moderování obsahu i pro komunikaci s orgány
            členských států a Evropskou komisí slouží adresa{' '}
            <a href="mailto:info@matevolno.cz" className="underline hover:text-slate-600">
              info@matevolno.cz
            </a>
            . Komunikačním jazykem je čeština, případně angličtina. Podrobnosti najdete v článku 11{' '}
            <a
              href="/docs/matevolno_vop_b2c.pdf"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-slate-600"
            >
              obchodních podmínek
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  )
}
